import type { ApiV1 } from '@teamworksdev/influencer/core';
import {
	type ApiClient,
	type FetchClient,
	type RequestMeta,
	Influencer,
} from '@teamworksdev/influencer/sdk';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { isNil } from 'lodash-es';
import type { Plugin } from 'vue';
import { injectionKeys } from '@/utils/injectionKeys';
import { isAfter } from 'date-fns';

declare module '@teamworksdev/influencer/sdk' {
	interface RequestMeta {
		/**
		 * If true, while impersonating, this request should use the auth token for the impersonator
		 * and not that of the user being impersonated
		 *
		 * @defaultvalue false
		 */
		bypassImpersonation?: boolean;
	}
}

export class InfluencerWeb {
	#client: Influencer;

	api: ApiClient;

	constructor() {
		this.#client = new Influencer({
			production: import.meta.env.MODE.startsWith('production'),
			serverUrl: import.meta.env.VITE_API_BASE,
			fetch: this.createCustomFetch(),
		});
		this.api = this.#client.api;
	}

	private log(message: string, data?: any) {
		if (import.meta.env.MODE.startsWith('production') || import.meta.env.MODE === 'test') return;
		console.debug(['[InfluencerWeb] ', message, data && ':'].join(''), data);
	}

	private getAuthToken(bypassImpersonation?: boolean): string | null {
		// Right now, the authStore is the source of truth
		// with regard to user authentication during migration.
		//
		// Once we're fully migrated to the Influencer SDK,
		// we will rely on that as our source of truth and
		// deprecate the need for the authStore.
		const authStore = useAuthStore();
		const token = authStore.token;
		const impersonationToken = authStore.impersonationToken;

		if (isNil(token)) {
			return null;
		}

		const isTokenExpired = isAfter(new Date(), new Date(token.expires_at));
		if (isTokenExpired) {
			return null;
		}

		if (bypassImpersonation || isNil(impersonationToken)) {
			return token.access_token;
		}

		const isImpersonationTokenExpired = isAfter(new Date(), new Date(impersonationToken.expires_at));
		if (isImpersonationTokenExpired) {
			return null;
		}

		return impersonationToken.access_token;
	}

	private getProfileId(bypassImpersonation?: boolean): string | null {
		const userStore = useUserStore();
		if (bypassImpersonation) {
			return userStore.inflcrImpersonatedById ?? null;
		}
		return userStore.inflcrId ?? null;
	}

	private hasUserAcceptedTou(): boolean {
		const userStore = useUserStore();
		return userStore.hasUserAcceptedLatestTermsOfUse;
	}

	private defaultRequestMeta: Readonly<RequestMeta> = {
		bypassImpersonation: false,
	};

	private createCustomFetch(): FetchClient {
		return async (url: string, init?: RequestInit, meta: RequestMeta = {}): Promise<Response> => {
			meta = {
				...this.defaultRequestMeta,
				...meta,
			};

			this.log('Request initiated', { url, init, meta });

			// Check terms of use if required
			if (meta.requiresUser && meta.requiresTou && !this.hasUserAcceptedTou()) {
				throw new Error('User has not accepted Terms of Use');
			}

			// Build headers
			const headers = new Headers(init?.headers);

			// Add auth header if required
			if (meta.requiresUser) {
				const token = this.getAuthToken(meta.bypassImpersonation);
				if (isNil(token)) {
					const authStore = useAuthStore();
					authStore.clearClientToken();
					throw new Error('Unauthenticated');
				} else {
					headers.set('Authorization', `Bearer ${token}`);
				}
			}

			// Add profile header if required
			if (meta.requiresProfile) {
				const profileId = this.getProfileId(meta.bypassImpersonation);
				if (isNil(profileId)) {
					throw new Error('No active profile available');
				} else {
					headers.set('Inflcr-Id', profileId);
				}
			}

			// Make the request
			return await fetch(url, {
				...init,
				headers,
			});
		};
	}

	async logIn(payload: ApiV1.BasicAuthRequest) {
		const token = await this.api.auth.login(payload);

		const authStore = useAuthStore();
		authStore.setToken(token, payload.remember_me);
	}

	async logOut() {
		await this.api.auth.logout({ bypassImpersonation: true });

		const authStore = useAuthStore();
		authStore.clearClientToken();
	}
}

export const InfluencerWebPlugin: Plugin = {
	install: (app) => {
		const inflcr = new InfluencerWeb();
		app.provide(injectionKeys.INFLUENCER, inflcr);
	},
};
